import React, { useRef } from 'react';
import styled, { keyframes } from 'styled-components';

import { useModal } from '../../../../Providers/ModalContext';
import Modal from '../../../../Global/Modal';
import RichText from '../../../../Global/RichText';

import CloseIcon from '../../../../../Images/icons/close-icon.png';
import DirectorBg from '../../../../../Images/background/bg_single-director.png';
import LogoStudio from '../../../../../Images/logos/logo-studio.png';
import PlayIcon from '../../../../../Images/icons/icon-play.png';

import { Directors_Title } from '../../../../../utils/ES/Modals/directors';

const bgIn = keyframes`
  0% {
    opacity: 0;
    background-position: 640px 0px;
  }
  100% {
    opacity: 1
    background-position: top left;
  }
`;

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
  desktop: '1440px',
};

const CloseContainer = styled.div`
  padding: 10px;
  position: absolute;
  top: 30px;
  right: 30px;

  @media (max-width: ${breakpoints.mobile}) {
    top: 15px;
    right: 15px;
    width: 60px;
    height: 60px;
  }
`;

const CloseButton = styled.img`
  cursor: pointer;
  width: 64px;
  height: auto;
  transition: all 0.3s ease-out;
  transform-origin: center;

  @media (max-width: ${breakpoints.mobile}) {
    width: 40px;
  }

  &:hover {
    filter: drop-shadow(0px 0px 10px rgba(255, 255, 255, 1))
      drop-shadow(0px 0px 4px rgba(255, 255, 255, 1));
    transform: scale(1.15);
  }
`;

const ModalContent = styled.div`
  width: 100%;
  position: relative;
  padding: 25px 0px 0px 0px;
  display: flex;
  align-items: flex-start;

  flex-direction: column;
  gap: 0.8rem;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 25px 0 0 0 !important;
  }
`;

const TitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: 3.375rem;
  padding-right: 5%;
  padding-left: 110px;

  @media (max-width: ${breakpoints.mobile}) {
    padding-left: 30px;
  }

  p,
  h2,
  h3 {
    text-transform: uppercase;
  }
`;

const LogoContent = styled.div`
  display: block;
  max-width: 276px;
  width: 100%;
  padding: 15px;
  position: relative;

  img {
    display: block;
    width: 100%;
    cursor: pointer;
    transition: all 0.3s ease-out;

    &:hover {
      filter: drop-shadow(0px 0px 20px rgba(250, 115, 19, 1));
    }
  }

  &::after {
    content: '';
    position: absolute;
    width: 12px;
    height: 93px;
    background: #fff;

    top: 0;
    bottom: 0;

    margin: auto;
    right: -10%;
  }
`;

const DirectorContainer = styled.div`
  position: relative;
  width: 100%;
  height: 917px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 15rem;
  background-image: url(${({ $directorImage = '' }) => $directorImage});
  background-size: cover;
  background-position: top left;
  background-repeat: no-repeat;
  padding: 20px 5% 4.5% 20px;

  @media (max-width: ${breakpoints.tablet}) {
    height: 100%;
    background-size: contain;
  }

  @media (max-width: ${breakpoints.mobile}) {
    height: 100%;
    align-items: center;
    justify-content: center;
    padding: 20px;
    padding-top: 227px;
    background-size: contain;
  }

  .director-name {
    text-align: center;
    padding-bottom: 5%;

    @media (max-width: ${breakpoints.tablet}) {
      position: absolute;
      top: 35%;
      left: 10%;
    }

    @media (max-width: ${breakpoints.mobile}) {
      position: absolute;
      padding-bottom: 0;
      top: 15%;
      left: 10%;
      font-size: 1rem !important;
    }
  }

  .open & {
    animation: ${bgIn} 1s both;
  }
`;

const DirectorInfo = styled.div`
  max-width: 1001px;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;

  @media (max-width: ${breakpoints.desktop}) {
    max-width: 900px;
  }

  @media (max-width: ${breakpoints.tablet}) {
    max-width: 560px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 100%;
  }
`;

const MainVideo = styled.div`
  position: relative;
  width: 100%;
  height: 431px;
  background-image: url(${({ $mainVideoBg = '' }) => $mainVideoBg});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 15px;
  cursor: pointer;

  @media (max-width: ${breakpoints.mobile}) {
    height: 207px;
  }

  h3 {
    font-size: 1.5rem;
    position: absolute;
    bottom: 20px;
    left: 20px;
    max-width: 777px;
    width: 100%;
    text-transform: uppercase;
  }
`;

const PlayButton = styled.img`
  max-width: ${({ $playMaxWidth = '160px' }) => $playMaxWidth};
  width: 100%;
  height: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 60px;
  }
`;

const InfoContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${breakpoints.tablet}) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  @media (max-width: ${breakpoints.mobile}) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const InfoRow = styled.div`
  position: relative;
  max-width: 221px;
  width: 100%;
  height: 411px;

  background-image: url(${({ $rowImage = '' }) => $rowImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  cursor: ${({ $cursor = 'auto' }) => $cursor};

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 48%;
    height: 380px;
  }

  .text-video {
    font-size: 1.5rem;
    position: absolute;
    bottom: 20px;
    left: 10px;
    text-transform: uppercase;
  }

  &.text {
    @media (max-width: ${breakpoints.mobile}) {
      position: absolute;
      top: 10px;
      right: 10px;
      max-width: 192px;
      height: auto;
    }
  }
`;

const ModalSingleDirector = ({
  id,
  mainBg,
  directorName,
  mainVideoBg,
  mainVideoLink,
  videoText1,
  directorText,
  rowImage1,
  videoText2,
  rowVideoLink1,
  rowImage2,
  videoText3,
  rowVideoLink2,
  rowImage3,
  videoText4,
  rowVideoLink3,
}) => {
  const { closeModal } = useModal();

  const { openModal } = useModal();

  const handleClick = (e) => {
    const target = e.currentTarget.dataset.target;
    openModal(target);
  };

  return (
    <>
      <Modal
        id={id}
        bgImg={DirectorBg}
        padding={'40px 0px'}
        mobPadding={'20px 0px'}
      >
        <CloseContainer>
          <CloseButton
            onClick={closeModal}
            aria-label="Close modal"
            src={CloseIcon}
          />
        </CloseContainer>
        <ModalContent>
          <TitleContent>
            <LogoContent>
              <img
                src={LogoStudio}
                onClick={closeModal}
                aria-label="Close modal"
                alt="Kuter Studio"
              />
            </LogoContent>
            <RichText dataText={Directors_Title} />
          </TitleContent>
          <DirectorContainer $directorImage={mainBg}>
            <RichText className="director-name" dataText={directorName} />
            <DirectorInfo>
              <MainVideo
                $mainVideoBg={mainVideoBg}
                onClick={handleClick}
                data-target={mainVideoLink}
              >
                <PlayButton src={PlayIcon} alt="Play Video" />
                <RichText dataText={videoText1} />
              </MainVideo>
              <InfoContainer>
                <InfoRow className="text">
                  <RichText dataText={directorText} />
                </InfoRow>
                <InfoRow
                  $rowImage={rowImage1}
                  $cursor={'pointer'}
                  onClick={handleClick}
                  data-target={rowVideoLink1}
                >
                  <PlayButton
                    $playMaxWidth={'100px'}
                    src={PlayIcon}
                    alt="Play Video"
                  />
                  <RichText className="text-video" dataText={videoText2} />
                </InfoRow>
                <InfoRow
                  $rowImage={rowImage2}
                  $cursor={'pointer'}
                  onClick={handleClick}
                  data-target={rowVideoLink2}
                >
                  <PlayButton
                    $playMaxWidth={'100px'}
                    src={PlayIcon}
                    alt="Play Video"
                  />
                  <RichText className="text-video" dataText={videoText3} />
                </InfoRow>
                <InfoRow
                  $rowImage={rowImage3}
                  $cursor={'pointer'}
                  onClick={handleClick}
                  data-target={rowVideoLink3}
                >
                  <PlayButton
                    $playMaxWidth={'100px'}
                    src={PlayIcon}
                    alt="Play Video"
                  />
                  <RichText className="text-video" dataText={videoText4} />
                </InfoRow>
              </InfoContainer>
            </DirectorInfo>
          </DirectorContainer>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ModalSingleDirector;
